import React from 'react'
import styled from '@emotion/styled'
import ReactMarkdown from 'react-markdown'
import { Tooltip } from '@retailer-platform/shared-components'
import { IconButton, Link, TDS_DEFAULT_COLORS } from '@retailer-platform/shared-components/src/tds'
import { DislikeIcon, LikeIcon } from '@instacart/ids-core'
import { FeedbackEnum } from '../../../__codegen__/api'
import { useDashMessages } from '../../../intl/intl.hooks'
import { useCommentModal } from './CommentModalContext'

interface ChatMessageProps {
  text: string
  sender: 'user' | 'ai'
  feedback?: FeedbackEnum
  comment?: string
  messageIndex: number
  onThumbsUp: () => void
  onThumbsDown: () => void
}

const MessageBubble = styled.div<{ sender: 'user' | 'ai' }>(({ sender }) => ({
  padding: '14px 14px',
  borderRadius: sender === 'user' ? 'none' : '16px 16px 16px 0px',
  maxWidth: '100%',
  wordWrap: 'break-word',
  alignSelf: sender === 'user' ? 'flex-end' : 'flex-start',
  backgroundColor:
    sender === 'ai'
      ? TDS_DEFAULT_COLORS.colors.systemGrayscale00
      : TDS_DEFAULT_COLORS.colors.systemGrayscale10,
  color: TDS_DEFAULT_COLORS.colors.systemGrayscale80,
  marginLeft: sender === 'user' ? 'auto' : '0', // Ensures user messages are on the right
  marginRight: sender === 'ai' ? 'auto' : '0', // Ensures AI messages are on the left
  lineHeight: '20px',
  '& p': {
    margin: 0,
  },
  '& li': {
    margin: '4px 0',
  },
  '& li img': {
    display: 'inline-block',
    width: '32px',
    height: '32px',
    objectFit: 'cover',
    borderRadius: '4px',
    marginRight: '8px',
    verticalAlign: 'middle',
    border: '1px solid rgba(0, 0, 0, 0.1)',
  },
  '& img': {
    display: 'inline-block',
    width: '32px',
    height: '32px',
    objectFit: 'cover',
    borderRadius: '4px',
    marginRight: '8px',
    verticalAlign: 'middle',
    border: '1px solid rgba(0, 0, 0, 0.1)',
  },
  '& pre': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    padding: '8px',
    borderRadius: '4px',
    overflowX: 'auto',
    margin: '8px 0',
  },
  '& code': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    padding: '2px 4px',
    borderRadius: '4px',
    fontSize: '0.9em',
  },
  '& ul': {
    margin: '8px 0',
    paddingLeft: '20px',
    listStyleType: 'disc',
    listStylePosition: 'outside',
  },
  '& ol': {
    margin: '8px 0',
    paddingLeft: '20px',
    listStyleType: 'decimal',
    listStylePosition: 'outside',
  },
  '& blockquote': {
    borderLeft: '4px solid rgba(0, 0, 0, 0.2)',
    margin: '8px 0',
    padding: '4px 0 4px 12px',
    color: 'rgba(0, 0, 0, 0.7)',
  },
  '& p img + br': {
    display: 'none',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '12px 0 8px 0',
    lineHeight: '1.3',
  },
  '& h1': { fontSize: '1.4em' },
  '& h2': { fontSize: '1.3em' },
  '& h3': { fontSize: '1.2em' },
  '& h4': { fontSize: '1.1em' },
  '& h5, & h6': { fontSize: '1em' },
  '& a': {
    color: TDS_DEFAULT_COLORS.colors.brandPrimaryRegular,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  '& hr': {
    border: 'none',
    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
    margin: '12px 0',
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: '8px 0',
  },
  '& th, & td': {
    border: '1px solid rgba(0, 0, 0, 0.1)',
    padding: '6px 8px',
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
}))

const MessageContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
})

const MessageActions = styled.div({
  display: 'flex',
  marginLeft: '4px',
})

const MessageAction = styled.div(() => ({
  cursor: 'pointer',
  marginRight: '8px',
}))

const MessageLink = styled.div(() => ({
  marginLeft: 'auto',
  display: 'flex',
}))

export const ChatMessage: React.FC<ChatMessageProps> = ({
  text,
  sender,
  feedback,
  comment,
  messageIndex,
  onThumbsUp,
  onThumbsDown,
}) => {
  const { openCommentModal } = useCommentModal()

  const intlMessages = useDashMessages({
    markAsHelpful: 'aiAgent.chatMessage.feedback.markAsHelpful',
    removePositiveFeedback: 'aiAgent.chatMessage.feedback.removePositiveFeedback',
    markAsUnhelpful: 'aiAgent.chatMessage.feedback.markAsUnhelpful',
    removeNegativeFeedback: 'aiAgent.chatMessage.feedback.removeNegativeFeedback',
    addComment: 'aiAgent.chatMessage.feedback.addComment',
    editComment: 'aiAgent.chatMessage.feedback.editComment',
  })

  const handleAddCommentClick = () => {
    openCommentModal(messageIndex, comment)
  }

  return (
    <MessageContainer>
      <MessageBubble sender={sender} data-testid="message-bubble">
        <ReactMarkdown
          components={{
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
          }}
        >
          {text}
        </ReactMarkdown>
      </MessageBubble>
      {sender === 'ai' && (
        <MessageActions>
          <Tooltip
            target={
              <MessageAction onClick={onThumbsUp} data-testid="thumbs-up-button">
                <IconButton
                  icon={LikeIcon}
                  variant="tertiary"
                  data-hovered={feedback === FeedbackEnum.Positive}
                />
              </MessageAction>
            }
            containerStyles={{ zIndex: 10000 }}
          >
            {feedback === FeedbackEnum.Positive
              ? intlMessages.removePositiveFeedback
              : intlMessages.markAsHelpful}
          </Tooltip>
          <Tooltip
            target={
              <MessageAction onClick={onThumbsDown} data-testid="thumbs-down-button">
                <IconButton
                  icon={DislikeIcon}
                  variant="tertiary"
                  data-hovered={feedback === FeedbackEnum.Negative}
                />
              </MessageAction>
            }
            containerStyles={{ zIndex: 10000 }}
          >
            {feedback === FeedbackEnum.Negative
              ? intlMessages.removeNegativeFeedback
              : intlMessages.markAsUnhelpful}
          </Tooltip>

          {feedback !== undefined && (
            <MessageLink>
              <Link onClick={handleAddCommentClick} data-testid="add-comment-link">
                {comment ? intlMessages.editComment : intlMessages.addComment}
              </Link>
            </MessageLink>
          )}
        </MessageActions>
      )}
    </MessageContainer>
  )
}
